import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
const BASE_URL = process.env.REACT_APP_API_URL;
const tiers = [
  { min: 1, max: 22, rate: 10 },
  { min: 23, max: 31, rate: 35 },
  { min: 32, max: 50, rate: 70 },
  { min: 51, max: 79, rate: 95 },
  { min: 80, max: Infinity, rate: 130 },
];

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const CommissionTracker = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const agentId = query.get('agentId');
  const agentName = query.get('agentName');
  const selectedMonth = parseInt(query.get('month'));
  const selectedYear = parseInt(query.get('year'));

  const [fullUnits, setFullUnits] = useState(0);
  const [commissionRate, setCommissionRate] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);
  const [nextTier, setNextTier] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!agentId || isNaN(selectedMonth) || isNaN(selectedYear)) return;

    const fetchOrders = async () => {
      setLoading(true);
      try {
        const gm = await axios.get(`${BASE_URL}/api/agents?id=${agentId}`);
        const mob = await axios.get(`${BASE_URL}/api/mobility?id=${agentId}`);

        const gmData = gm.data || [];
        const mobData = (mob.data || []).map(m => ({
          ...m,
          Units: 0.5,
          Activation_Date: m.GM_ACTIVATION_DT,
        }));

        const combined = [...gmData, ...mobData];

        const filtered = combined.filter(order => {
          const date = new Date(order.Activation_Date);
          return (
            date.getMonth() === selectedMonth &&
            date.getFullYear() === selectedYear
          );
        });

        const totalUnits = filtered.reduce((acc, o) => acc + parseFloat(o.Units), 0);
        setFullUnits(totalUnits);

        const tier = tiers.find(t => totalUnits >= t.min && totalUnits <= t.max) || tiers[0];
        setCommissionRate(tier.rate);
        setTotalCommission(totalUnits * tier.rate);

        const nextTierInfo = tiers.find(t => t.min > totalUnits);
        if (nextTierInfo) {
          setNextTier({
            needed: nextTierInfo.min - totalUnits,
            rate: nextTierInfo.rate,
            total: nextTierInfo.min * nextTierInfo.rate,
          });
        } else {
          setNextTier(null);
        }
      } catch (err) {
        console.error('Error fetching commission data:', err);
      }
      setLoading(false);
    };

    fetchOrders();
  }, [agentId, selectedMonth, selectedYear]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white px-6 py-10">
      <div className="max-w-4xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-10 shadow-2xl">
        <h1 className="text-4xl font-bold text-center mb-6">💸 Commission Tracker</h1>
        <h2 className="text-center text-white mt-2 text-sm">
          Agent ID: {agentId}
        </h2>

        <h2 className="text-center text-xl font-semibold mt-6 mb-8">
          ✅ Installations in {months[selectedMonth]} {selectedYear}
        </h2>

        {loading ? (
          <p className="text-center text-gray-300">Loading data...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 p-6 rounded-lg shadow">
              <p className="text-gray-300">Total Full Units</p>
              <p className="text-4xl font-extrabold mt-2">{fullUnits}</p>
            </div>
            <div className="bg-slate-800 p-6 rounded-lg shadow">
              <p className="text-gray-300">Current Commission Rate</p>
              <p className="text-4xl font-extrabold mt-2">${commissionRate}</p>
            </div>
            <div className="bg-slate-800 p-6 rounded-lg shadow">
              <p className="text-gray-300">Total Commission</p>
              <p className="text-4xl font-extrabold mt-2">${totalCommission.toFixed(2)}</p>
            </div>
            {nextTier && (
              <div className="bg-slate-800 p-6 rounded-lg shadow">
                <p className="text-gray-300">Next Tier</p>
                <p className="mt-2 text-xl">{nextTier.needed} more units needed</p>
                <p className="mt-2">Next Rate: <span className="font-semibold">${nextTier.rate}</span></p>
                <p>Potential: <span className="font-semibold">${nextTier.total}</span></p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionTracker;
